/**
 * Contrato da camada neutra de banco (/api/v1/bank/*).
 * Espelho de backend/src/services/bank/BankAdapter.js — ver docs/CONTAS_BANCARIAS.md.
 */

export type BankProvider = 'sicoob' | 'efi';

export interface BankAccountDTO {
  id: string;
  companyId: string;
  companyName?: string;
  provider: BankProvider;
  providerLabel: 'Sicoob' | 'Efí';
  label: string;
  accountNumber: string | null;
  branch: string | null;
  environment: string | null;
  isDefault: boolean;
  status: string;
  configured: boolean;
}

export interface BankFeatures {
  saldo: boolean;
  extrato: boolean;
  pixRecebidos: boolean;
  pixCobranca: boolean;
  pixEnviar: boolean;
  pixEnviados: boolean;
  boletos: boolean;
  dda: boolean;
  ted: boolean;
  transferenciaInterna: boolean;
  conciliacao: boolean;
  webhooks: boolean;
  /** venda por link de pagamento (cartão/boleto) — API Cobranças Efí */
  cobrancaLink: boolean;
}

export interface BankCapabilities {
  provider: BankProvider;
  features: BankFeatures;
}

/** Capabilities conhecidas por provedor (o backend é a fonte de verdade; isto é o fallback do menu) */
export const BANK_FEATURES: Record<BankProvider, BankFeatures> = {
  sicoob: {
    saldo: true, extrato: true, pixRecebidos: true, pixCobranca: true, pixEnviar: true, pixEnviados: true,
    boletos: true, dda: true, ted: true, transferenciaInterna: true, conciliacao: false, webhooks: true, cobrancaLink: false,
  },
  efi: {
    saldo: true, extrato: true, pixRecebidos: true, pixCobranca: true, pixEnviar: true, pixEnviados: true,
    boletos: false, dda: false, ted: false, transferenciaInterna: false, conciliacao: true, webhooks: true, cobrancaLink: true,
  },
};

export const PROVIDER_LABEL: Record<BankProvider, string> = { sicoob: 'Sicoob', efi: 'Efí' };

export interface Saldo {
  disponivel: number;
  limite: number;
  bloqueado: number;
  total: number;
  moeda: 'BRL';
  atualizadoEm: string;
  raw?: unknown;
}

export interface Lancamento {
  id: string;
  data: string;
  descricao: string;
  valor: number;
  tipo: 'C' | 'D';
  categoria?: string;
  contraparteNome?: string;
  contraparteDocumento?: string;
  e2eId?: string;
  txid?: string;
  saldoApos?: number | null;
  raw?: unknown;
}

export interface Extrato {
  periodo: { inicio: string; fim: string };
  saldoInicial: number | null;
  saldoFinal: number | null;
  totalCreditos: number;
  totalDebitos: number;
  lancamentos: Lancamento[];
  avisos?: string[];
}

export interface PixRecebido {
  e2eId: string;
  txid: string | null;
  valor: number;
  horario: string;
  pagadorNome: string | null;
  pagadorDocumento: string | null;
  infoPagador: string | null;
  devolucoes: { id: string; valor: number; status: string }[];
  raw?: unknown;
}

export interface Cobranca {
  txid: string;
  valor: number;
  status: string;
  pixCopiaECola: string;
  imagemQrcode: string | null;
  criadoEm: string;
  expiraEm: string | null;
  raw?: unknown;
}

export interface EnvioPix {
  idEnvio: string;
  e2eId: string | null;
  valor: number;
  status: 'EM_PROCESSAMENTO' | 'REALIZADO' | 'NAO_REALIZADO';
  horario: string;
  favorecido: { nome?: string; documento?: string; chave?: string };
  raw?: unknown;
}
